import { FC, FormEvent, useEffect, useRef } from 'react';

import { Button, Heading, Paragraph, TextSpacing } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import Icon from '@telialt/common-icon/src';
import { TextArea } from '@telialt/common-text-area/src';
import { TextInput } from '@telialt/common-text-input/src';
import type { LeadFormTrackingContext } from '@telialt/gtm-tracking/src/types';
import { SegmentType } from '@telialt/models/src/SegmentType';
import { useHybrisSegment } from '@telialt/utils/src/segment';

import {
    INPUT_NAMES,
    LEAD_NAME_MIN_LENGTH,
    LEAD_NAME_PATTERN,
    LEAD_PHONE_MOBILE_PATTERN,
    LEAD_PHONE_PREFIX,
} from '../../constants';
import { useGAEvent } from '../../hooks/useGAEvent';
import { LeadGAEventEcommerce, LeadInfoProps, LeadSubmitRequestData } from '../../model';
import { LeadTestID } from '../../test-ids';
import { HelpRow, HelpText, HelpTooltip, LeadInfoForm } from './LeadInfo.styles';

type LeadInfoSpecificProps = {
    isLoading: boolean;
    initialData?: LeadSubmitRequestData;
    onSubmit: (event: FormEvent) => void;
    gaEventEcommerce?: LeadGAEventEcommerce;
    gaTracking?: LeadFormTrackingContext;
};

export const LeadFormHelpTooltipID = 'help-tooltip';

export const LeadInfo: FC<LeadInfoProps & LeadInfoSpecificProps> = ({
    title,
    description,
    helpText,
    helpTooltip,
    hideHelpTooltip,
    complementaryComment,
    showComplementaryComment,
    isLoading,
    initialData,
    onSubmit,
    gaEventEcommerce,
    gaTracking,
}) => {
    const { t } = useTranslations();
    const segment = useHybrisSegment();

    const defaultDescription = t['lead.default.description'];
    const defaultHelpText = t['lead.default.help_text'];
    const defaultHelpTooltip = t['lead.default.help_tooltip'];
    const defaultComplementaryComment = t['lead.default.complementary_text'];

    const { pushGAEvent } = useGAEvent(gaTracking, gaEventEcommerce);
    const pushGAEventRef = useRef(pushGAEvent);
    pushGAEventRef.current = pushGAEvent;
    const formRef = useRef<HTMLFormElement>(null);

    // Push event on first input in any of the form fields
    useEffect(() => {
        const formElement = formRef.current;
        const inputElements = formElement?.querySelectorAll('input, textarea') || [];

        const handleInput = () => {
            pushGAEventRef.current('formStart');
            removeListeners();
        };

        const addListeners = () => {
            inputElements.forEach((input) => {
                input.addEventListener('input', handleInput);
            });
        };

        addListeners();

        const removeListeners = () => {
            inputElements.forEach((input) => {
                input.removeEventListener('input', handleInput);
            });
        };

        return () => {
            removeListeners();
        };
    }, []);

    return (
        <TextSpacing>
            <Heading tag="h2" variant="title-400">
                {title || t['lead.default.title']}
            </Heading>
            <Paragraph>{description || defaultDescription}</Paragraph>
            <LeadInfoForm name="lead-form" onSubmit={onSubmit} data-test={LeadTestID.LEAD_INFO_FORM} ref={formRef}>
                {segment === SegmentType.B2c ? (
                    <TextInput
                        dataTest={INPUT_NAMES.NAME}
                        name={INPUT_NAMES.NAME}
                        label={t['lead.label.name']}
                        pattern={LEAD_NAME_PATTERN.source}
                        patternErrorMessage={t['lead.message.name_error']}
                        minLength={LEAD_NAME_MIN_LENGTH}
                        minLengthErrorMessage={t['lead.message.name_error']}
                        defaultValue={initialData?.p_common_name || ''}
                    />
                ) : (
                    <TextInput
                        dataTest={INPUT_NAMES.COMPANY_CODE}
                        name={INPUT_NAMES.COMPANY_CODE}
                        label={t['lead.label.company_name']}
                        defaultValue={initialData?.p_b2b_code || ''}
                        required={true}
                        requiredErrorMessage={t['lead.message.company_name_error']}
                    />
                )}
                <TextInput
                    type="tel"
                    inputMode="tel"
                    required={true}
                    pattern={LEAD_PHONE_MOBILE_PATTERN.source}
                    patternErrorMessage={t['lead.message.phone_error']}
                    requiredErrorMessage={t['lead.message.phone_error']}
                    dataTest={INPUT_NAMES.PHONE_NUMBER}
                    name={INPUT_NAMES.PHONE_NUMBER}
                    label={t['lead.label.phone']}
                    defaultValue={initialData?.p_telephone_number || LEAD_PHONE_PREFIX}
                />
                <TextArea
                    dataTest={INPUT_NAMES.COMMENT}
                    label={t['lead.label.comment']}
                    rows={2}
                    name={INPUT_NAMES.COMMENT}
                    defaultValue={initialData?.p_comment || ''}
                    helperText={
                        showComplementaryComment ? complementaryComment || defaultComplementaryComment : undefined
                    }
                />
                <HelpRow>
                    <HelpText>{helpText || defaultHelpText}</HelpText>
                    {!hideHelpTooltip && (
                        <HelpTooltip
                            id={LeadFormHelpTooltipID}
                            content={helpTooltip || defaultHelpTooltip}
                            ariaLabel={t['common.tooltip.aria_label']}
                        >
                            <Icon type="info-circle" />
                        </HelpTooltip>
                    )}
                </HelpRow>
                <div>
                    <Button type="submit" variant="primary" disabled={isLoading}>
                        {t['lead.label.submit']}
                    </Button>
                </div>
            </LeadInfoForm>
        </TextSpacing>
    );
};
